import { Controller } from "@hotwired/stimulus";
import debounce from "debounce";

const CTRL = "categories--admin--campaigns--matchallenges--paginated-competing-designations";

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

// Connects to data-controller="categories--admin--campaigns--matchallenges--paginated-competing-designations"
export default class extends Controller {
  static targets = [
    "addedList", "hiddenFields", "addedSearch", "addedPagination", "addedEmpty",
    "addAllButton", "leftEmpty", "leftSearch", "leftList", "leftPagination",
  ];

  static MIN_SEARCH_LENGTH = 3;

  static outlets = [CTRL];

  static values = {
    group: Boolean,
    frameId: String,
    allNodesUrl: String,
    initialCompetitorsUrl: String,
    initialAvailableUrl: String,
  };

  static PAGE_SIZE = 10;

  connect() {
    this.selectedIds = new Set();
    this.selectAllActive = false;
    this.clientSideLeftPanel = false;
    this.removedFromAll = new Set();
    this.addedPage = 1;
    this.leftPage = 1;
    this.allNodes = [];
    this.filterAdded = debounce(this.filterAdded.bind(this), 300);
    this.filterLeft = debounce(this.filterLeft.bind(this), 300);
    this.frame = document.getElementById(this.frameIdValue);
    if (this.frame) {
      this.onFrameLoad = () => this.hideSelectedInFrame();
      this.frame.addEventListener("turbo:frame-load", this.onFrameLoad);
    }

    if (this.hasInitialCompetitorsUrlValue && this.initialCompetitorsUrlValue) {
      this.loadExistingCompetitors();
    }
  }

  disconnect() {
    if (this.frame && this.onFrameLoad) {
      this.frame.removeEventListener("turbo:frame-load", this.onFrameLoad);
    }
  }

  // --- Initialization ---

  async loadExistingCompetitors() {
    this.loadingExisting = true;
    this.showRightLoadingSpinner();
    this.showLeftLoadingSpinner();
    this.toggleLoadingState(true);
    this.toggleAddAllButton(false);

    try {
      const response = await fetch(this.initialCompetitorsUrlValue, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content,
        },
      });

      if (!response.ok) {
        console.error(`Failed to load competitors: ${response.status}`); // eslint-disable-line no-console
        this.addedListTarget.innerHTML = "<li class=\"p-4 text-center text-red-500\">Failed to load competitors. Please try again.</li>";
        return;
      }

      const competitors = await response.json();

      this.addedListTarget.innerHTML = "";
      await this.buildItemsProgressively(competitors);
      this.initialIds = new Set(this.selectedIds);
      this.syncHiddenFields();
      this.renderAddedPage();
      this.hideSelectedInFrame();

      if (this.hasInitialAvailableUrlValue && this.initialAvailableUrlValue) {
        await this.loadInitialAvailableNodes();
      }
    } catch (error) {
      console.error("Error loading existing competitors:", error); // eslint-disable-line no-console
      this.addedListTarget.innerHTML = "<li class=\"p-4 text-center text-red-500\">Failed to load competitors. Please try again.</li>";
    } finally {
      this.loadingExisting = false;
      this.hideLeftLoadingSpinner();
      this.toggleLoadingState(false);
      // Show empty state if left panel has no available items
      if (this.clientSideLeftPanel && this.hasLeftListTarget && this.leftListTarget.children.length === 0 && this.hasLeftEmptyTarget) {
        this.leftEmptyTarget.textContent = "All designations have been added.";
        this.leftEmptyTarget.classList.remove("hidden");
      }
      const searchHasText = this.hasLeftSearchTarget && this.leftSearchTarget.value.trim().length > 0;
      this.toggleAddAllButton(!searchHasText);
    }
  }

  async loadInitialAvailableNodes() {
    const response = await fetch(this.initialAvailableUrlValue, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content,
      },
    });
    if (!response.ok) return;
    const nodes = await response.json();
    this.clientSideLeftPanel = true;
    if (this.frame) this.frame.style.display = "none";
    nodes.forEach(({ id, name }) => this.buildLeftItem(id.toString(), name));
    if (this.hasLeftListTarget) this.leftListTarget.classList.remove("hidden");
    this.renderLeftPage();
  }

  // --- Public actions ---

  preventEnterSubmit(event) { // eslint-disable-line class-methods-use-this
    event.preventDefault();
  }

  resetOtherLists() {
    this.categoriesAdminCampaignsMatchallengesPaginatedCompetingDesignationsOutlets.forEach((outlet) => {
      outlet.removeAll();
    });
    this.syncHiddenFields();
  }

  addCompetitor({ params }) {
    const competitorId = params.id.toString();
    if (this.selectedIds.has(competitorId)) return;
    this.selectedIds.add(competitorId);
    this.removedFromAll.delete(competitorId);

    // Get name from frame item or left list item
    let name = competitorId;
    const frameEl = this.frameItem(competitorId);
    if (frameEl) {
      name = frameEl.querySelector(".flex-1").textContent;
      frameEl.closest("li").style.display = "none";
    }

    // Remove from client-side left list if present (after Add All or in edit mode)
    if (this.selectAllActive || this.clientSideLeftPanel) {
      const leftItem = this.hasLeftListTarget && this.leftListTarget.querySelector(`[data-competitor-id="${competitorId}"]`);
      if (leftItem) {
        if (!name || name === competitorId) name = leftItem.querySelector(".flex-1").textContent;
        leftItem.remove();
      }
      this.renderLeftPage();
    }

    this.buildAddedItem(competitorId, name);
    this.syncHiddenFields();
    this.renderAddedPage();
  }

  removeCompetitor({ params }) {
    const competitorId = params.id.toString();
    this.selectedIds.delete(competitorId);

    const addedItem = this.addedListTarget.querySelector(`[data-competitor-id="${competitorId}"]`);
    const name = addedItem ? addedItem.querySelector(".flex-1").textContent : competitorId;
    if (addedItem) addedItem.remove();

    if (this.selectAllActive) {
      this.removedFromAll.add(competitorId);
      // Build a client-side left item instead of relying on the turbo frame
      this.buildLeftItem(competitorId, name);
      this.showLeftPanel();
      this.renderLeftPage();
    } else if (this.clientSideLeftPanel) {
      this.buildLeftItem(competitorId, name);
      this.renderLeftPage();
    } else {
      // Show the item back in the turbo frame
      const frameEl = this.frameItem(competitorId);
      if (frameEl) {
        frameEl.closest("li").style.display = "";
      }
    }

    this.syncHiddenFields();
    this.renderAddedPage();
  }

  async addAll() {
    if (!this.allNodesUrlValue || this.addingAll || this.loadingExisting) return;
    this.addingAll = true;
    this.toggleAddAllButton(false);
    this.toggleLoadingState(true);
    this.showRightLoadingSpinner();
    this.hideLeftPanel("All designations will be added.");

    try {
      const response = await fetch(this.allNodesUrlValue, {
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content },
      });
      const nodes = await response.json();

      this.selectAllActive = true;
      this.allNodes = nodes;
      this.removedFromAll.clear();
      this.selectedIds.clear();
      this.addedListTarget.innerHTML = "";
      if (this.hasLeftListTarget) this.leftListTarget.innerHTML = "";
      await this.buildItemsProgressively(nodes);
      this.hideSelectedInFrame();
      this.syncHiddenFields();
      this.renderAddedPage();
    } finally {
      this.addingAll = false;
      this.toggleLoadingState(false);
    }
  }

  removeAll() {
    this.selectedIds.clear();
    this.selectAllActive = false;
    this.removedFromAll.clear();
    this.allNodes = [];
    this.addedPage = 1;
    this.leftPage = 1;
    this.addedListTarget.innerHTML = "";
    this.setBanner(null);

    // Restore turbo frame, hide client-side left list
    if (this.frame) {
      this.frame.style.display = "";
      this.showAllFrameItems();
    }
    if (this.hasLeftListTarget) {
      this.leftListTarget.innerHTML = "";
      this.leftListTarget.classList.add("hidden");
    }
    if (this.hasLeftPaginationTarget) {
      this.leftPaginationTarget.innerHTML = "";
      this.leftPaginationTarget.classList.add("hidden");
    }
    if (this.hasLeftEmptyTarget) {
      this.leftEmptyTarget.textContent = "";
      this.leftEmptyTarget.classList.add("hidden");
    }

    if (this.hasHiddenFieldsTarget) this.hiddenFieldsTarget.innerHTML = "";
    this.renderAddedPage();
    this.toggleAddAllButton(true);
  }

  filterAdded() {
    const term = this.hasAddedSearchTarget ? this.addedSearchTarget.value.trim() : "";
    if (term.length > 0 && term.length < this.constructor.MIN_SEARCH_LENGTH) {
      this.showAddedEmpty(`Enter at least ${this.constructor.MIN_SEARCH_LENGTH} characters to search`);
      this.hideAllListItems(this.addedListTarget);
      if (this.hasAddedPaginationTarget) this.addedPaginationTarget.innerHTML = "";
      return;
    }
    this.addedPage = 1;
    this.renderAddedPage();
  }

  changeAddedPage({ params }) {
    this.addedPage = params.page;
    this.renderAddedPage();
  }

  filterLeft() {
    const term = this.hasLeftSearchTarget ? this.leftSearchTarget.value.trim() : "";

    // Disable "Add All" while searching, re-enable when cleared
    if (!this.addingAll && !this.loadingExisting && !this.selectAllActive) {
      this.toggleAddAllButton(term.length === 0);
    }

    if (!this.selectAllActive && !this.clientSideLeftPanel) return;
    if (term.length > 0 && term.length < this.constructor.MIN_SEARCH_LENGTH) {
      if (this.hasLeftEmptyTarget) {
        this.leftEmptyTarget.textContent = `Enter at least ${this.constructor.MIN_SEARCH_LENGTH} characters to search`;
        this.leftEmptyTarget.classList.remove("hidden");
      }
      this.hideAllListItems(this.leftListTarget);
      if (this.hasLeftPaginationTarget) this.leftPaginationTarget.innerHTML = "";
      return;
    }
    this.leftPage = 1;
    this.renderLeftPage();
  }

  changeLeftPage({ params }) {
    this.leftPage = params.page;
    this.renderLeftPage();
  }

  // --- Right panel (added items) rendering ---

  renderAddedPage() {
    const term = this.hasAddedSearchTarget ? this.addedSearchTarget.value.trim().toLowerCase() : "";
    const items = Array.from(this.addedListTarget.children);
    const matching = items.filter((el) => !term || el.textContent.toLowerCase().includes(term));
    const { PAGE_SIZE: pageSize, MIN_SEARCH_LENGTH: minLen } = this.constructor;
    const totalPages = Math.max(1, Math.ceil(matching.length / pageSize));
    if (this.addedPage > totalPages) this.addedPage = totalPages;
    const start = (this.addedPage - 1) * pageSize;
    const end = start + pageSize;

    this.hideAllListItems(this.addedListTarget);
    this.showItemsInRange(matching, start, end);

    // Show "no results" when searching with no matches
    if (term.length >= minLen && matching.length === 0) {
      this.showAddedEmpty(`No designations found for "${term}".`);
    } else {
      this.showAddedEmpty(null);
    }

    this.renderPagination(this.addedPaginationTarget, totalPages, this.addedPage, "changeAddedPage");
  }

  // --- Left panel (removed items after Add All) rendering ---

  renderLeftPage() {
    if (!this.hasLeftListTarget) return;
    const term = this.hasLeftSearchTarget ? this.leftSearchTarget.value.trim().toLowerCase() : "";
    const items = Array.from(this.leftListTarget.children);
    const matching = items.filter((el) => !term || el.textContent.toLowerCase().includes(term));
    const { PAGE_SIZE: pageSize } = this.constructor;
    const totalPages = Math.max(1, Math.ceil(matching.length / pageSize));
    if (this.leftPage > totalPages) this.leftPage = totalPages;
    const start = (this.leftPage - 1) * pageSize;
    const end = start + pageSize;

    this.hideAllListItems(this.leftListTarget);
    this.showItemsInRange(matching, start, end);

    // Show "no results" message when searching with no matches
    if (this.hasLeftEmptyTarget) {
      const searching = term.length > 0;
      const noResults = searching && matching.length === 0;
      this.leftEmptyTarget.textContent = noResults ? `No designations found for "${term}".` : "";
      this.leftEmptyTarget.classList.toggle("hidden", !noResults);
    }

    if (this.hasLeftPaginationTarget) {
      this.renderPagination(this.leftPaginationTarget, totalPages, this.leftPage, "changeLeftPage");
      this.leftPaginationTarget.classList.toggle("hidden", items.length === 0);
    }

    // Hide left list if empty (all items re-added to right)
    if (items.length === 0 && this.selectAllActive) {
      this.hideLeftPanel("All designations will be added.");
    }
  }

  showLeftPanel() {
    if (this.frame) this.frame.style.display = "none";
    if (this.hasLeftEmptyTarget) {
      this.leftEmptyTarget.textContent = "";
      this.leftEmptyTarget.classList.add("hidden");
    }
    if (this.hasLeftListTarget) this.leftListTarget.classList.remove("hidden");
    if (this.hasLeftPaginationTarget) this.leftPaginationTarget.classList.remove("hidden");
  }

  hideLeftPanel(message) {
    if (this.frame) this.frame.style.display = "none";
    if (this.hasLeftListTarget) this.leftListTarget.classList.add("hidden");
    if (this.hasLeftPaginationTarget) {
      this.leftPaginationTarget.innerHTML = "";
      this.leftPaginationTarget.classList.add("hidden");
    }
    if (this.hasLeftEmptyTarget) {
      this.leftEmptyTarget.textContent = message || "";
      this.leftEmptyTarget.classList.toggle("hidden", !message);
    }
  }

  // --- Shared pagination renderer ---

  renderPagination(paginationTarget, totalPages, currentPage, action) { // eslint-disable-line class-methods-use-this
    if (!paginationTarget) return;
    if (totalPages <= 1) {
      paginationTarget.innerHTML = ""; // eslint-disable-line no-param-reassign
      return;
    }

    const btn = (p, label, disabled) => disabled
      ? `<span class="px-2 py-1 text-gray-300">${label}</span>`
      : `<button type="button" class="px-2 py-1 text-gray-600 hover:text-primary-blue cursor-pointer" data-action="click->${CTRL}#${action}" data-${CTRL}-page-param="${p}">${label}</button>`;
    const links = [btn(currentPage - 1, "&lt;", currentPage === 1)];

    for (let p = 1; p <= totalPages; p += 1) {
      if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
        links.push(p === currentPage
          ? `<span class="px-2 py-1 font-bold border border-gray-300 rounded">${p}</span>`
          : btn(p, p, false));
      } else if (p === currentPage - 2 || p === currentPage + 2) {
        links.push("<span class=\"px-1 text-gray-400\">...</span>");
      }
    }

    links.push(btn(currentPage + 1, "&gt;", currentPage === totalPages));
    paginationTarget.innerHTML = `<div class="flex items-center justify-center gap-1 px-4 py-3">${links.join("")}</div>`; // eslint-disable-line no-param-reassign
  }

  // --- Progressive loading ---

  buildItemsProgressively(nodes) {
    const BATCH_SIZE = 100;
    const total = nodes.length;
    let built = 0;

    const firstBatch = nodes.slice(0, this.constructor.PAGE_SIZE);
    firstBatch.forEach(({ id, name }) => {
      const nodeId = id.toString();
      this.selectedIds.add(nodeId);
      this.buildAddedItem(nodeId, name);
    });
    built = firstBatch.length;
    this.renderAddedPage();
    this.setBanner(`Loading ${built} of ${total} designations...`);

    if (built >= total) {
      this.setBanner(null);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const buildBatch = () => {
        const batch = nodes.slice(built, built + BATCH_SIZE);
        batch.forEach(({ id, name }) => {
          const nodeId = id.toString();
          this.selectedIds.add(nodeId);
          this.buildAddedItem(nodeId, name);
        });
        built += batch.length;
        this.setBanner(`Loading ${built} of ${total} designations...`);
        this.renderAddedPage();

        if (built < total) {
          setTimeout(buildBatch, 0);
        } else {
          this.setBanner(null);
          resolve();
        }
      };
      setTimeout(buildBatch, 0);
    });
  }

  // --- UI helpers ---

  setBanner(text) {
    const existing = this.addedListTarget.parentElement.querySelector("[data-add-all-banner]");
    if (!text) { if (existing) existing.remove(); return; }
    const banner = existing || (() => { const el = document.createElement("div"); el.dataset.addAllBanner = ""; el.className = "p-3 text-center text-gray-500 text-sm border-b"; return el; })();
    banner.textContent = text;
    if (!existing) this.addedListTarget.before(banner);
  }

  showAddedEmpty(text) {
    if (!this.hasAddedEmptyTarget) return;
    this.addedEmptyTarget.textContent = text || "";
    this.addedEmptyTarget.classList.toggle("hidden", !text);
  }

  toggleAddAllButton(enabled) {
    if (!this.hasAddAllButtonTarget) return;
    this.addAllButtonTarget.disabled = !enabled;
    this.addAllButtonTarget.classList.toggle("text-primary-blue", enabled);
    this.addAllButtonTarget.classList.toggle("hover:text-primary-blue-dark", enabled);
    this.addAllButtonTarget.classList.toggle("text-gray-400", !enabled);
    this.addAllButtonTarget.classList.toggle("cursor-not-allowed", !enabled);
  }

  toggleLoadingState(loading) {
    if (this.hasAddedSearchTarget) this.addedSearchTarget.disabled = loading;
    if (this.hasAddedPaginationTarget) this.addedPaginationTarget.classList.toggle("pointer-events-none", loading);

    this.addedListTarget.querySelectorAll(".remove-button").forEach((removeBtn) => {
      removeBtn.disabled = loading; // eslint-disable-line no-param-reassign
      const svg = removeBtn.querySelector("svg");
      if (svg) {
        svg.classList.toggle("text-primary-blue", !loading);
        svg.classList.toggle("hover:text-primary-blue-dark", !loading);
        svg.classList.toggle("text-gray-300", loading);
      }
    });

    if (this.hasLeftSearchTarget) this.leftSearchTarget.disabled = loading;
    if (this.hasLeftPaginationTarget) this.leftPaginationTarget.classList.toggle("pointer-events-none", loading);
    if (this.frame) this.frame.classList.toggle("pointer-events-none", loading);

    if (this.hasLeftListTarget) {
      this.leftListTarget.querySelectorAll(".add-button").forEach((addBtn) => {
        addBtn.disabled = loading; // eslint-disable-line no-param-reassign
        const svg = addBtn.querySelector("svg");
        if (svg) {
          svg.classList.toggle("text-primary-blue", !loading);
          svg.classList.toggle("hover:text-primary-blue-dark", !loading);
          svg.classList.toggle("text-gray-300", loading);
        }
      });
    }

    const form = this.element.closest("form");
    const submitBtn = form && form.querySelector("[type=\"submit\"]");
    if (submitBtn) submitBtn.disabled = loading;
  }

  showRightLoadingSpinner() {
    this.addedListTarget.innerHTML = `<li class="p-4 text-center text-gray-500">
      <span class="inline-flex items-center gap-2">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Preparing to load
      </span>
    </li>`;
  }

  showLeftLoadingSpinner() {
    // Hide the turbo frame so it doesn't briefly flash server content during edit load
    if (this.frame) this.frame.style.display = "none";
    if (this.hasLeftEmptyTarget) {
      this.leftEmptyTarget.innerHTML = `<span class="inline-flex items-center gap-2">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading designations...
      </span>`;
      this.leftEmptyTarget.classList.remove("hidden");
    }
  }

  hideLeftLoadingSpinner() {
    if (this.hasLeftEmptyTarget) {
      this.leftEmptyTarget.textContent = "";
      this.leftEmptyTarget.classList.add("hidden");
    }
  }

  // --- DOM helpers ---

  // eslint-disable-next-line class-methods-use-this
  hideAllListItems(list) {
    list.querySelectorAll("li").forEach((el) => { el.style.display = "none"; }); // eslint-disable-line no-param-reassign
  }

  showAllFrameItems() {
    if (!this.frame) return;
    this.frame.querySelectorAll("li").forEach((el) => { el.style.display = ""; }); // eslint-disable-line no-param-reassign
  }

  // eslint-disable-next-line class-methods-use-this
  showItemsInRange(items, start, end) {
    items.forEach((el, i) => { el.style.display = (i >= start && i < end) ? "" : "none"; }); // eslint-disable-line no-param-reassign
  }

  // --- DOM builders ---

  buildAddedItem(id, name) {
    const loading = this.addingAll || this.loadingExisting;
    const svgColor = loading ? "text-gray-300" : "text-primary-blue hover:text-primary-blue-dark";
    const li = document.createElement("li");
    li.className = "pl-0";
    li.style.display = "none";
    li.dataset.competitorId = id;
    li.innerHTML = `
      <div class="border-b flex gap-2 p-2 pr-4">
        <div class="flex-1">${escapeHtml(name)}</div>
        <button type="button" class="remove-button" aria-label="Remove competitor"${loading ? " disabled" : ""}
          data-action="click->${CTRL}#removeCompetitor"
          data-${CTRL}-id-param="${id}">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 ${svgColor}" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>
          </svg>
        </button>
      </div>`;
    this.addedListTarget.appendChild(li);
  }

  buildLeftItem(id, name) {
    if (!this.hasLeftListTarget) return;
    const loading = this.addingAll || this.loadingExisting;
    const svgColor = loading ? "text-gray-300" : "text-primary-blue hover:text-primary-blue-dark";
    const li = document.createElement("li");
    li.className = "pl-0";
    li.style.display = "none";
    li.dataset.competitorId = id;
    li.innerHTML = `
      <div class="border-b flex gap-2 p-2 pr-4" data-id="${id}">
        <div class="flex-1">${escapeHtml(name)}</div>
        <button type="button" class="add-button" aria-label="Add competitor"${loading ? " disabled aria-disabled=\"true\"" : ""}
          data-action="click->${CTRL}#addCompetitor"
          data-${CTRL}-id-param="${id}">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 ${svgColor}" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10.75 4.75C10.75 4.33579 10.4142 4 10 4C9.58579 4 9.25 4.33579 9.25 4.75V9.25H4.75C4.33579 9.25 4 9.58579 4 10C4 10.4142 4.33579 10.75 4.75 10.75L9.25 10.75V15.25C9.25 15.6642 9.58579 16 10 16C10.4142 16 10.75 15.6642 10.75 15.25V10.75L15.25 10.75C15.6642 10.75 16 10.4142 16 10C16 9.58579 15.6642 9.25 15.25 9.25H10.75V4.75Z"/>
          </svg>
        </button>
      </div>`;
    this.leftListTarget.appendChild(li);
  }

  // --- Frame helpers ---

  frameItem(id) {
    return this.frame && this.frame.querySelector(`[data-id="${id}"]`);
  }

  hideSelectedInFrame() {
    if (!this.frame) return;

    if (this.selectAllActive) {
      this.frame.querySelectorAll("[data-id]").forEach((dataEl) => {
        if (!this.removedFromAll.has(dataEl.dataset.id)) {
          const li = dataEl.closest("li");
          if (li) li.style.display = "none";
        }
      });
      const pagination = this.frame.querySelector("[data-left-pagination]");
      if (pagination) pagination.style.display = "none";
      return;
    }

    this.selectedIds.forEach((id) => {
      const found = this.frameItem(id);
      if (found) {
        const li = found.closest("li");
        if (li) li.style.display = "none";
      }
    });
  }

  // --- Hidden fields sync ---

  syncHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return;
    const parts = [];

    if (this.selectAllActive) {
      parts.push("<input type=\"hidden\" name=\"contribution[matchallenge_attributes][wta_select_all]\" value=\"true\">");
      this.removedFromAll.forEach((id) => {
        parts.push(`<input type="hidden" name="contribution[matchallenge_attributes][wta_remove_competitor_ids][]" value="${id}">`);
      });
    } else if (this.initialIds) {
      // Edit delta mode: adds go into wta_competitor_ids (same param as create)
      this.selectedIds.forEach((id) => {
        if (!this.initialIds.has(id)) {
          parts.push(`<input type="hidden" name="contribution[matchallenge_attributes][wta_competitor_ids][]" value="${id}">`);
        }
      });
      this.initialIds.forEach((id) => {
        if (!this.selectedIds.has(id)) {
          parts.push(`<input type="hidden" name="contribution[matchallenge_attributes][wta_remove_competitor_ids][]" value="${id}">`);
        }
      });
    } else {
      // Create mode: send full list
      this.selectedIds.forEach((id) => {
        parts.push(`<input type="hidden" name="contribution[matchallenge_attributes][wta_competitor_ids][]" value="${id}">`);
      });
    }

    parts.push(`<input type="hidden" name="contribution[matchallenge_attributes][wta_group]" value="${this.groupValue}">`);
    this.hiddenFieldsTarget.innerHTML = parts.join("");
  }
}
